import type { DownloadLinkConfig } from 'configuration-schemas/confdefnView';
import { connect } from 'react-redux';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import { SizeEligibilityListGrid } from './SizeEligibilityListGrid';
import {
  requestSizeEligibilityListGridConfig,
  receiveSizeEligibilityListGridConfig,
  SizeEligibilityListGridConfigs,
  requestSizeEligibilityListGridGridData,
  receiveSizeEligibilityListGridGridData,
  receiveSizeEligibilitySizeRanges,
  receiveFloorsetData,
  receiveCompanionData,
  setSelectedSizeRange,
  setValidSizes,
  requestCompanionViewData,
  resetSlice,
  receiveError,
} from './SizeEligibilityListGrid.slice';
import container from 'src/ServiceContainer';
import { API_URL_PREFIX, ASSORTMENT } from 'src/utils/Domain/Constants';
import { selectAndProjectState } from './SizeEligibilityListGrid.selector';
import { SubheaderSlice, SubheaderViewDefns } from 'src/components/Subheader/Subheader.slice';
import { AppThunkDispatch } from 'src/store';

import {
  getValidValues,
  ValueLabelPair,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/StyleEditSection.client';
import ServiceContainer from 'src/ServiceContainer';
import { toast } from 'react-toastify';
import { BasicItem, BasicPivotItem } from 'src/worker/pivotWorker.types';
import { ClientDataApi } from 'configuration-schemas/confdefnView';
import axios from 'axios';
import { getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { ColDef } from '@ag-grid-community/core';
import type { SizeEligibilityListGridOwnProps } from 'configuration-schemas/ownProps';
import { DataApi, ListDataApi } from 'configuration-schemas/confdefnView';
import { flow } from 'lodash';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { AppType } from 'configuration-schemas/general';
import { CollapsibleCompanion } from 'src/common-ui/components/CompanionListView/CompanionListView';
import { bindActionCreators } from '@reduxjs/toolkit';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';

export interface SizeEligibilityGridRow {
  size_range_class: string;
  store_count: number;
  grade_cap: string;
  sizerangelvl: string;
  capacity: string;
  sizeattr: string[];
  size_eligibility: string[];
  size_min: number;
  size_min_weeks: number;
  size_min_strategy: string;
}
export interface LoadingSizeEligibilityList extends Pick<CollapsibleCompanion, 'isCompanionCollapsed'> {
  topProduct: string;
  dataLoading: true;
  configLoading: true;
}
export interface LoadedSizeEligibilityList extends Pick<CollapsibleCompanion, 'isCompanionCollapsed'> {
  topProduct: string;
  dataLoading: boolean;
  configLoading: boolean;
  companionLoading: boolean;
  gridData: BasicPivotItem[] | undefined;
  sizeRanges: ValueLabelPair[] | undefined;
  selectedSizeRange: ValueLabelPair | undefined;
  validSizes: string[] | undefined;
  viewDefns: SizeEligibilityListGridConfigs | undefined;
  floorsetData: BasicItem[] | undefined;
  companionData: BasicPivotItem[] | undefined;
  subheader: SubheaderSlice;
  subheaderDefns: SubheaderViewDefns | undefined;
  companionApi?: ListDataApi;
  dataApi: ListDataApi;
  downloadLink?: string | DownloadLinkConfig;
}
export interface ProjectedState extends LoadedSizeEligibilityList {
  locations: string[];
  sizeRanges: ValueLabelPair[];
  treeColumnDefinition: ColDef | undefined;
  sizeNames: string[];
}
const validsizeslookup = (id: string) =>
  `api/v2/dependents?appName=Assortment&source=ccsizerange&target=validsizes&id=${id}`;
interface DependencyLookupValues {
  value: string;
  label: string;
}
const getCompanionData = (companionApi: ClientDataApi | ListDataApi | undefined, dispatch: AppThunkDispatch) => {
  if (!companionApi) return;
  dispatch(requestCompanionViewData());
  if (companionApi.isListData) {
    ServiceContainer.pivotService
      .listData(companionApi.defnId, AppType.Assortment, companionApi.params)
      .then((resp) => {
        const data = resp.flat;
        dispatch(receiveCompanionData(data));
      })
      .catch((e) => {
        toast.error(`An error occured getting the companion view / or it was out of date and canceled`);
        ServiceContainer.loggingService.error(
          'An error occured getting the companion view / or it was out of date and canceled',
          e.stack,
        );
      });
  } else {
    const dataUrl = getUrl(companionApi);
    axios.get(dataUrl).then((_d) => {
      // TODO: Load some kind of data here
      dispatch(receiveCompanionData([]));
    });
  }
};
const getFloorsetData = async (floorsetApi: DataApi, dispatch: AppThunkDispatch) => {
  if (!floorsetApi || !!floorsetApi.isListData) return; // required
  const { url: floorsetUrl, headers: floorsetHeaders, params: floorsetParams } = floorsetApi;
  const floorsetOptions = {
    headers: floorsetHeaders,
    params: floorsetParams,
  };
  return axios
    .get(API_URL_PREFIX + floorsetUrl, floorsetOptions)
    .then((resp) => {
      const data: BasicItem[] = resp.data.data;
      dispatch(receiveFloorsetData(data));
      return data;
    })
    .catch((e) => {
      toast.info('An error occured getting the floorsets');
      ServiceContainer.loggingService.error('An error occured getting the floorsets', e.stack);
    });
};
const getGridData = (
  dataApi: ListDataApi,
  sizeRange: ValueLabelPair,
  floorset: string,
  dispatch: AppThunkDispatch,
  groupBy?: string,
) => {
  dispatch(requestSizeEligibilityListGridGridData());

  const aggByWithGroupBy =
    dataApi.params?.aggBy && groupBy ? `${groupBy},${dataApi.params.aggBy}` : dataApi.params?.aggBy;

  const gridDataPromise = container.pivotService.listData(dataApi.defnId, ASSORTMENT, {
    ...dataApi.params,
    aggBy: aggByWithGroupBy,
    topMembers: sizeRange.value + (floorset.length > 0 ? ',' : '') + floorset,
  });
  const validSizesProm = getValidValues(validsizeslookup(sizeRange.value), false, false, false);
  Promise.all([gridDataPromise, validSizesProm])
    .then((results) => {
      const [gridData, validSizes] = results;
      // TODO: fix this shameful assertion
      const projectedGridData: BasicPivotItem[] = gridData.tree.map((loc: BasicPivotItem) => {
        //const mappedLoc = (loc as unknown) as SizeEligibilityGridRowPayload;
        return {
          ...loc,
          sizeattr: [],
          size_eligibility: loc['attribute:selected_sizes:name'],
        };
      });
      dispatch(setValidSizes(validSizes.map((i: DependencyLookupValues) => i.value)));
      return dispatch(receiveSizeEligibilityListGridGridData(projectedGridData));
    })
    .catch((error) => {
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.sizeEligibility,
          issues: error,
          defnId: error.defnId,
        }),
      );
    });
};

const getSizeRangeThenGetGridData = (
  dataApi: ListDataApi,
  topProduct: string,
  floorset: string,
  dispatch: AppThunkDispatch,
  groupBy?: string,
) => {
  const validValuesPromise = getValidValues(
    `/api/attribute/validValues/one?appName=Assortment&ids=ccsizerange&members=${topProduct}`,
    false,
    false,
    false,
  );
  return validValuesPromise
    .then((sizeRanges) => {
      dispatch(receiveSizeEligibilitySizeRanges(sizeRanges));
      dispatch(setSelectedSizeRange(sizeRanges[0]));
      return getGridData(dataApi, sizeRanges[0], floorset, dispatch, groupBy);
    })
    .catch((e) => {
      toast.info('An error occured getting the valid size ranges');
      ServiceContainer.loggingService.error('An error occured getting the valid size ranges', e.stack);
    });
};

function dispatchToProps(dispatch: AppThunkDispatch, ownProps: SizeEligibilityListGridOwnProps) {
  const { defns, viewProperty, companionApi, floorsetApi, dataApi } = ownProps;
  return {
    ...bindActionCreators({ onToggleCompanion: toggleCompanionCollapse }, dispatch),
    groupByDefn: defns.subheader.groupBy,
    viewProperty,
    onShowView(topProduct: string | undefined, groupBy?: string) {
      dispatch(requestSizeEligibilityListGridConfig());
      getCompanionData(companionApi, dispatch);
      container.tenantConfigClient
        .getTenantViewDefns<SizeEligibilityListGridConfigs>({
          defnIds: defns.view,
          appName: ASSORTMENT,
        })
        .then(async (config) => {
          const maybeExcConfig = config[0];
          if (maybeExcConfig.columns && maybeExcConfig.columns !== undefined && maybeExcConfig.main) {
            // TODO: actually use this
            dispatch(receiveSizeEligibilityListGridConfig(maybeExcConfig));
          }
          if (topProduct) {
            if (floorsetApi) {
              const data = await getFloorsetData(floorsetApi, dispatch);
              return getSizeRangeThenGetGridData(
                dataApi,
                topProduct,
                (data as BasicPivotItem[])[0].id,
                dispatch,
                groupBy,
              );
            }
            return getSizeRangeThenGetGridData(dataApi, topProduct, '', dispatch, groupBy);
          }
          return undefined;
        })
        .catch((error) => {
          dispatch(
            receiveError({
              type: ComponentErrorType.config,
              message: (error as Error)?.message,
              name: ConfDefnComponentType.sizeEligibility,
              issues: error,
              defnId: error.defnId,
            }),
          );
        });
    },
    updateData(newData: BasicPivotItem[]) {
      dispatch(receiveSizeEligibilityListGridGridData(newData));
    },
    onRefetchData(topProduct: string | undefined, floorset: string, groupBy: string | undefined) {
      getCompanionData(companionApi, dispatch);
      if (floorsetApi) {
        getFloorsetData(floorsetApi, dispatch);
      }
      if (topProduct) {
        getSizeRangeThenGetGridData(dataApi, topProduct, floorset, dispatch, groupBy);
      }
    },
    handleChangeSizeRange(newSizeRange: ValueLabelPair, floorset: string, groupBy: string | undefined) {
      dispatch(setSelectedSizeRange(newSizeRange));
      getGridData(dataApi, newSizeRange, floorset, dispatch, groupBy);
    },
    onUnmount() {
      dispatch(resetSlice());
    },
  };
}
export interface SizeEligibilityListGridDispatchProps extends ReturnType<typeof dispatchToProps> {}

const sensitiveView = flow(() => SizeEligibilityListGrid, makeScopeAndFilterSensitive, makePrintSensitive)();

export default connect(selectAndProjectState, dispatchToProps)(sensitiveView);
